import * as amplitude from '@amplitude/analytics-browser';
import { BrowserClient, ValidPropertyType } from '@amplitude/analytics-types';
import {
    Experiment,
    ExperimentClient,
    ExperimentConfig,
    ExperimentUser,
} from '@amplitude/experiment-js-client';
import { logDebugInsights, logEvent, logException } from './logging';
import { getPlatform, getUrlParam } from './helpers';
import { translateAmplitudeFFs } from './utils/translateAmplitudeFF';
import isObjectEmpty from './utils/isObjectEmpty';

/**
 * Feature flag type.
 */
export type AmpFlagValue = string | boolean | number;

/**
 * A map of feature flags from their keys to their values.
 */
export interface AmpFlagSet {
    [key: string]: AmpFlagValue;
}

class AnalyticsClient {
    // The experiment client is an API availabe to work with feature flags and experiments
    experimentClient?: ExperimentClient;
    // and analytics client is an API available to help report events and user properties.
    analyticsClient?: BrowserClient;
    identify: amplitude.Identify;

    constructor() {
        this.identify = new amplitude.Identify();
    }

    insightsFullName = (detail: string) => `Amplitude - ${detail}`;

    /**
     * Initializes the Amplitude Experiment Client for work with Feature Flags and Experiments.
     * @param amplitudeDeploymentKey The Amplitude deployment key.
     * @param amplitudeAnalyticsKey The Amplitude analytics key.
     * @param amplitudeReverseProxyEndpoint The url of the reverse proxy.
     * @param properties
     * @returns {Promise<boolean>} A promise that resolves to true if the client was initialized successfully.
     */
    async initializeExperimentClient(
        amplitudeDeploymentKey: string,
        amplitudeReverseProxyEndpoint: string,
        userId?: string,
        properties?: Record<string, ValidPropertyType>
    ): Promise<boolean | undefined> {
        const user = this.initializeExperimentUser(userId, properties);

        try {
            const amplitudeExperimentClientConfig: ExperimentConfig = {
                // This prevents the client from polling for flag updates every minute.
                pollOnStart: false,
                serverUrl: amplitudeReverseProxyEndpoint,
                flagsServerUrl: amplitudeReverseProxyEndpoint,
                retryFetchOnFailure: false, // We are going to try once per page load and fail gracefully if it doesn't work.
            };

            const amplitudeExperimentClient =
                Experiment.initializeWithAmplitudeAnalytics(
                    amplitudeDeploymentKey,
                    amplitudeExperimentClientConfig
                );

            // We need to clear any previously cached data, otherwise the experiment client
            // will use stale data if the fetch fails and we have no way of knowing that.
            amplitudeExperimentClient.clear();

            // fetch vs start used for remove evaluation of flags.
            // Relevant amplitude docs:
            // https://amplitude.com/docs/feature-experiment/local-evaluation
            // https://amplitude.com/docs/feature-experiment/remote-evaluation
            await amplitudeExperimentClient.fetch(user);
            this.experimentClient = amplitudeExperimentClient;
            return true;
        } catch (err) {
            logException(
                this.insightsFullName('Init Error - ExperimentClient'),
                {
                    exception: err as Error,
                }
            );
        }
        return false;
    }

    /**
     * Initializes the Amplitude Client for work with Event reporting
     */
    initializeAnalyticsClient(
        amplitudeAnalyticsKey: string,
        userId?: string,
        amplitudeReverseProxyEndpoint?: string
    ): BrowserClient | undefined {
        if (this.analyticsClient) {
            return this.analyticsClient;
        }

        let analyticsClient;

        try {
            analyticsClient = amplitude.createInstance();
            analyticsClient.init(amplitudeAnalyticsKey, userId, {
                flushQueueSize: 0,
                defaultTracking: false,
                fetchRemoteConfig: false, // Remote configs dont play nicely with proxies.
                serverUrl:
                    (amplitudeReverseProxyEndpoint &&
                        `${amplitudeReverseProxyEndpoint}/2/httpapi`) ||
                    undefined,
            });
            this.analyticsClient = analyticsClient;
            return analyticsClient;
        } catch (err) {
            logException(
                this.insightsFullName('Init Error - AnalyticsClient'),
                {
                    exception: err as Error,
                }
            );
        }
        return analyticsClient;
    }

    initializeExperimentUser(
        userId?: string,
        properties?: Record<string, ValidPropertyType>
    ): ExperimentUser {
        const user: ExperimentUser = {};

        // only set user id if available
        if (userId) {
            user['user_id'] = userId;
        }

        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        const user_properties = {} as Record<string, any>;
        for (const [key, value] of Object.entries(properties || {})) {
            user_properties[key] = value;
        }
        user.user_properties = user_properties;

        return user;
    }

    /**
     * Fetches the feature flags from Amplitude.
     * @returns {AmpFlagSet} A map of feature flags from their keys to their values.
     */
    async fetchFlags(): Promise<AmpFlagSet> {
        const featureFlags: AmpFlagSet = {};

        if (!this.experimentClient) {
            console.error(
                'Amplitude Experiment Client not initialized, cannot fetch flags'
            );
            logException(
                this.insightsFullName(
                    'Amplitude Experiment Client not initialized'
                ),
                {
                    exception: new Error(
                        'Unable to fetch feature flags - client not initialized'
                    ),
                }
            );
            return featureFlags;
        }

        const allVariants = this.experimentClient.all();
        if (!allVariants || isObjectEmpty(allVariants)) {
            const errMsg = 'Failed to fetch Amplitude feature flags';
            logException(errMsg, { exception: new Error(errMsg) });
            return featureFlags;
        }

        logEvent({
            name: 'Amplitude feature flags fetched',
            properties: { flags: allVariants },
        });

        const translatedFeatureFlags: AmpFlagSet =
            translateAmplitudeFFs(allVariants);
        logEvent({
            name: 'Amplitude feature flags translated',
            properties: { flags: translatedFeatureFlags },
        });
        console.log('Amplitude flags: ', translatedFeatureFlags);
        return translatedFeatureFlags;
    }

    logUndefinedClient(funcName: string, properties = {}) {
        logDebugInsights({
            name: this.insightsFullName('AmplitudeClient undefined'),
            properties: { funcName, ...properties },
        });
    }
    flushEventsAsync = async () => {
        try {
            if (this.analyticsClient) {
                return this.analyticsClient.flush().promise;
            } else {
                this.logUndefinedClient('flushAmplitudeEventsAsync');
            }
        } catch (err) {
            logException(
                this.insightsFullName('flushAmplitudeEventsAsync Error'),
                {
                    exception: err as Error,
                }
            );
        }
    };

    getUserId = () => {
        try {
            if (this.analyticsClient) {
                return this.analyticsClient.getUserId();
            } else {
                this.logUndefinedClient('getAmplitudeUserId', {});
                return '';
            }
        } catch (err) {
            logException(this.insightsFullName('GetUserId Error'), {
                exception: err as Error,
            });
        }
    };

    getDeviceId = (): string => {
        return getUrlParam('ampdid');
    };

    setUserId = (userId: string) => {
        try {
            this.analyticsClient
                ? this.analyticsClient.setUserId(userId)
                : this.logUndefinedClient('setAmplitudeUserId', { userId });
        } catch (err) {
            logException(this.insightsFullName('SetUserId Error'), {
                exception: err as Error,
            });
        }
    };

    setSessionId = (sessionId: number) => {
        try {
            this.analyticsClient
                ? this.analyticsClient.setSessionId(sessionId)
                : this.logUndefinedClient('setAmplitudeSessionId', {
                      sessionId,
                  });
        } catch (err) {
            logException(this.insightsFullName('SetSessionId Error'), {
                exception: err as Error,
            });
        }
    };

    setDeviceId = (deviceId: string) => {
        try {
            this.analyticsClient
                ? this.analyticsClient.setDeviceId(deviceId)
                : this.logUndefinedClient('setAmplitudeDeviceId', { deviceId });
        } catch (err) {
            logException(this.insightsFullName('SetDeviceId Error'), {
                exception: err as Error,
            });
        }
    };

    logEvent = (eventName: string, eventDetails?: object) => {
        if (!eventDetails) {
            eventDetails = {};
        }

        if (
            typeof eventDetails === 'object' &&
            !Array.isArray(eventDetails) &&
            eventDetails !== null
        ) {
            (eventDetails as any)['host_platform'] = getPlatform();
        }

        try {
            this.analyticsClient
                ? this.analyticsClient.logEvent(eventName, eventDetails)
                : this.logUndefinedClient('logAmplitudeEvent', { eventName });
        } catch (err) {
            logException(this.insightsFullName('LogEvent Error'), {
                exception: err as Error,
            });
        }
    };

    setUserProperties = (
        properties: Record<string, ValidPropertyType>,
        analyticsClient: BrowserClient | undefined = this.analyticsClient
    ) => {
        try {
            for (const [key, value] of Object.entries(properties || {})) {
                this.identify.set(key, value);
            }
            analyticsClient
                ? analyticsClient.identify(this.identify)
                : this.logUndefinedClient('setAmplitudeUserProperties', {
                      ...(properties || {}),
                  });
        } catch (err) {
            logException(this.insightsFullName('SetUserProperties Error'), {
                exception: err as Error,
            });
        }
    };

    isInitialized = (): boolean => {
        if (!this.analyticsClient) {
            logDebugInsights({
                name: this.insightsFullName('IsAmplitudeInitialized'),
                properties: {
                    value: false,
                },
            });
        }
        return !!this.analyticsClient;
    };

    reset() {
        this.analyticsClient = undefined;
        this.experimentClient = undefined;
    }
}

export const amplitudeClients = new AnalyticsClient();
